import { useLayoutEffect, useState } from 'react';
import { useTheme } from '../components/ThemeProvider';

/**
 * Recharts needs literal colour strings, not Tailwind class names. This is the
 * sanctioned way to get them, so panels never hardcode a hex value (which is a
 * lint error and an AGENTS.md convention violation).
 *
 * Prefer the `useChartColors()` hook below over calling this directly — it is
 * the only form guaranteed to read the CURRENT theme's values.
 */
export function token(name: string): string {
  if (typeof document === 'undefined') return 'currentColor';
  const v = getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim();
  return v === '' ? 'currentColor' : `rgb(${v})`;
}

export interface ChartColors {
  readonly price: string;
  readonly powerLaw: string;
  readonly floor: string;
  readonly grid: string;
  readonly axis: string;
  readonly tooltipBg: string;
  readonly tooltipInk: string;
}

export const chartColors = (): ChartColors => ({
  price: token('a-btc'),
  powerLaw: token('accent'),
  floor: token('positive'),
  grid: token('line'),
  axis: token('ink-subtle'),
  tooltipBg: token('panel'),
  tooltipInk: token('ink'),
});

/**
 * The sanctioned way for a panel to get chart colours.
 *
 * Why a hook and not `useMemo(() => chartColors(), [theme])`: a child's render
 * runs before the ThemeProvider's effects, so a render-time read can sample the
 * outgoing theme. ThemeProvider now applies the `dark` class during its own
 * render to close that window, and this hook re-reads on the next frame as a
 * second guarantee — correct even if the class application is ever moved back
 * into an effect.
 */
export function useChartColors(): ChartColors {
  const { theme } = useTheme();
  const [colors, setColors] = useState<ChartColors>(() => chartColors());

  useLayoutEffect(() => {
    setColors(chartColors());
    // Re-read after paint: cheap, and it makes the hook robust to any future
    // change in when the theme class lands.
    const id = requestAnimationFrame(() => setColors(chartColors()));
    return () => cancelAnimationFrame(id);
  }, [theme]);

  return colors;
}
